import { Button } from '@/components/button';
import ExcelUploadForm from '@/components/form/excelupload';
import { FormLabel } from '@/components/form/formlabel';
import { Modal } from '@/components/modal';
import { AppDispatch } from '@/root/store';
import excelIcon from '@/routes/org/event/slug/ots/payments/_assets/excel.png';
import { StoreBulkCheckout } from '@/services/ots';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import { DocumentCheckIcon } from '@heroicons/react/24/solid';
import { useEchoPublic } from '@laravel/echo-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';

export interface SetupModalBase extends React.HTMLAttributes<HTMLDivElement> {
  uploadModal: boolean;
  setUploadModal?: any;
}

export const OtsUploadModal: React.FC<SetupModalBase> = ({
  uploadModal = false,
  setUploadModal,
}) => {
  const [attributes, setAttributes] = useState({
    file: File,
  });
  const { event } = useSelector((state: any) => state.event);
  const { totalRows } = useSelector((state: any) => state.helpers);
  const { errors } = useSelector((state: any) => state.ots);
  const [rowErrors, setRowErrors] = useState<Record<number, string[]>>({});
  const dispatch: AppDispatch = useDispatch();
  const [uploading, setUploading] = useState({
    total_rows: 0,
    success_rows: 0,
    failed_rows: 0,
  });
  const [isUploading, setIsUploading] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') ?? '');

  useEchoPublic(
    `upload-progress.${currentUser.id}.Organizer`,
    '.upload-progress',
    (e: any) => {
      setIsUploading(true);
      setUploading({
        total_rows: e.number_rows,
        success_rows: e.number_success,
        failed_rows: e.number_fails,
      });

      if (e.errors && typeof e.errors === 'object') {
        setRowErrors(e.errors);
      }

      if (e.done >= e.number_rows) {
        setIsUploading(false);

        if (e.number_rows === e.number_success) {
          toast.success('Berhasil mengupload semua data pembeli');

          window.location.href = '';
        } else if (e.number_fails > 0) {
          toast.error('Tidak berhasil mengupload semua data pembeli');
        }

        setTimeout(() => {
          toast.remove();

          setUploading({
            total_rows: 0,
            success_rows: 0,
            failed_rows: 0,
          });
        }, 4000);
      }
    }
  );

  useEffect(() => {
    if (uploading.total_rows <= 0) {
      return;
    }

    toast.custom(
      (t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } pointer-events-auto flex w-full max-w-md rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5`}
        >
          <div className='w-0 flex-1 p-4'>
            <div className='flex items-start'>
              <div className='flex-shrink-0 pt-0.5'>
                <img
                  className='h-12 w-12 rounded-full'
                  src={excelIcon}
                  alt=''
                />
              </div>
              <div className='ml-3 flex-1'>
                <p className='text-sm font-medium text-gray-900'>
                  Sedang Diproses
                </p>
                <p className='mt-1 text-sm text-gray-500'>
                  {uploading.success_rows} / {uploading.total_rows} telah sukses
                  diupload{' '}
                  <span className={'text-red-500'}>
                    ({uploading.failed_rows} gagal diupload)
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
      {
        duration: isUploading ? Infinity : 2000,
        id: 'upload-progress',
      }
    );
  }, [uploading, isUploading]);

  return (
    <Modal
      show={uploadModal}
      onRequestClose={() => setUploadModal(false)}
      width={'large'}
      title='Tambah pembeli sekaligus'
      body={
        <div className={'w-full'}>
          <FormLabel
            label='File data pembeli (.xlsx)'
            errorText={errors.balance}
          >
            <ExcelUploadForm
              onChange={(e: any) => {
                setAttributes({
                  file: e,
                });
              }}
              rowErrors={rowErrors}
            />
          </FormLabel>
        </div>
      }
      footer={
        <div className='flex justify-end gap-3'>
          <Button
            variant='neutral'
            onClick={() => setUploadModal(false)}
            leftIcon={<NoSymbolIcon />}
          >
            Batal
          </Button>
          <Button
            onClick={() => {
              dispatch(StoreBulkCheckout(event.id, attributes));
            }}
            disabled={totalRows === 0 || isUploading}
            leftIcon={<DocumentCheckIcon />}
          >
            Beli {totalRows} Tiket
          </Button>
        </div>
      }
    />
  );
};

OtsUploadModal.displayName = 'OtsUploadModal';
